import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { 
  ArrowLeft, Sparkles, Copy, RefreshCw, Loader2, Zap,
  Hash, MessageSquare, Target, Lightbulb, CheckCircle2,
  Rocket, Camera, HelpCircle, Gift, TrendingUp, Users
} from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'sonner';

// Content Templates for quick start
const CONTENT_TEMPLATES = [
  { 
    id: 'product_launch', 
    label: 'Product Launch', 
    icon: Rocket,
    topic: 'Announcing a new product or feature launch',
    tone: 'engaging',
    color: 'from-purple-500 to-pink-500'
  },
  { 
    id: 'behind_scenes', 
    label: 'Behind the Scenes', 
    icon: Camera,
    topic: 'A behind-the-scenes look at my work/day/process',
    tone: 'casual',
    color: 'from-blue-500 to-cyan-500'
  },
  { 
    id: 'tips_tricks', 
    label: 'Tips & Tricks', 
    icon: Lightbulb,
    topic: 'Sharing helpful tips and tricks in my niche',
    tone: 'educational',
    color: 'from-amber-500 to-orange-500'
  },
  { 
    id: 'qa_session', 
    label: 'Q&A Session', 
    icon: HelpCircle,
    topic: 'Answering common questions from my audience',
    tone: 'casual',
    color: 'from-green-500 to-emerald-500'
  },
  { 
    id: 'giveaway', 
    label: 'Giveaway/Contest', 
    icon: Gift,
    topic: 'Running a giveaway or contest for my followers',
    tone: 'engaging',
    color: 'from-pink-500 to-rose-500'
  },
  { 
    id: 'trending', 
    label: 'Trending Topic', 
    icon: TrendingUp,
    topic: 'My take on a trending topic in my industry',
    tone: 'professional',
    color: 'from-violet-500 to-purple-500'
  },
  { 
    id: 'testimonial', 
    label: 'Customer Story', 
    icon: Users,
    topic: 'Sharing a customer success story or testimonial',
    tone: 'inspirational',
    color: 'from-teal-500 to-cyan-500'
  },
  { 
    id: 'motivation', 
    label: 'Motivation Monday', 
    icon: Zap,
    topic: 'Motivational content to inspire my audience',
    tone: 'inspirational',
    color: 'from-orange-500 to-red-500'
  },
];

const DailyDropAIGenerator = () => {
  const navigate = useNavigate();
  const [niche, setNiche] = useState('');
  const [topic, setTopic] = useState('');
  const [tone, setTone] = useState('engaging');
  const [platform, setPlatform] = useState('instagram');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedContent, setGeneratedContent] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const tones = [
    { id: 'engaging', label: 'Engaging', emoji: '🎯' },
    { id: 'funny', label: 'Funny', emoji: '😂' },
    { id: 'inspirational', label: 'Inspirational', emoji: '✨' },
    { id: 'professional', label: 'Professional', emoji: '💼' },
    { id: 'casual', label: 'Casual', emoji: '😎' },
    { id: 'educational', label: 'Educational', emoji: '📚' },
  ];

  const platforms = [
    { id: 'instagram', label: 'Instagram' },
    { id: 'tiktok', label: 'TikTok' },
    { id: 'twitter', label: 'X (Twitter)' },
    { id: 'facebook', label: 'Facebook' },
    { id: 'youtube', label: 'YouTube' },
  ];

  const applyTemplate = (template) => {
    setTopic(template.topic);
    setTone(template.tone);
    setSelectedTemplate(template.id);
    toast.success(`Template applied: ${template.label}`);
  };

  const handleGenerate = async () => {
    if (!topic.trim()) {
      toast.error('Please enter a topic or idea');
      return;
    }

    setIsGenerating(true);
    try {
      const response = await api.post('/daily-drop/ai/generate', {
        niche,
        topic,
        tone,
        platform
      });
      setGeneratedContent(response.data);
      toast.success('Content generated!');
    } catch (error) {
      console.error('Error generating content:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate content');
    } finally {
      setIsGenerating(false);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied!`);
  };

  const useContent = () => {
    if (generatedContent) {
      const params = new URLSearchParams({
        caption: generatedContent.caption || '',
        hashtags: generatedContent.hashtags || '',
        cta: generatedContent.cta || ''
      });
      navigate(`/daily-drop/create?${params.toString()}`);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900">
      {/* Header */}
      <header className="border-b border-purple-500/20 bg-black/30 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="icon"
              className="text-purple-200 hover:text-white hover:bg-purple-500/20"
              onClick={() => navigate('/daily-drop')}
              data-testid="back-button"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold text-white flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-amber-400" />
                AI Content Generator
              </h1>
              <p className="text-xs text-purple-300">Create viral captions, hashtags & hooks</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Quick Templates Section */}
        <Card className="bg-black/30 border-purple-500/20 backdrop-blur-sm mb-6">
          <CardHeader className="pb-2">
            <CardTitle className="text-white text-sm flex items-center gap-2">
              <Rocket className="h-4 w-4 text-purple-400" />
              Quick Templates - Click to start
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {CONTENT_TEMPLATES.map((template) => {
                const IconComponent = template.icon;
                return (
                  <Button
                    key={template.id}
                    variant="outline"
                    className={`h-auto py-3 flex flex-col items-center gap-1 border-purple-500/30 hover:border-purple-400 transition-all ${
                      selectedTemplate === template.id ? 'bg-purple-500/20 border-purple-400' : ''
                    }`}
                    onClick={() => applyTemplate(template)}
                    data-testid={`template-${template.id}`}
                  >
                    <div className={`bg-gradient-to-r ${template.color} p-1.5 rounded`}>
                      <IconComponent className="h-4 w-4 text-white" />
                    </div>
                    <span className="text-xs text-purple-200 text-center">{template.label}</span>
                  </Button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Input Section */}
          <div className="space-y-6">
            <Card className="bg-black/30 border-purple-500/20 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Lightbulb className="h-5 w-5 text-amber-400" />
                  What's Your Content About?
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-purple-200">Your Niche (optional)</Label>
                  <Input
                    value={niche}
                    onChange={(e) => setNiche(e.target.value)}
                    placeholder="e.g., fitness, cooking, tech..."
                    className="bg-purple-500/10 border-purple-500/30 text-white placeholder:text-purple-300/50"
                    data-testid="niche-input"
                  />
                </div>
                <div>
                  <Label className="text-purple-200">Topic or Idea *</Label>
                  <Textarea
                    value={topic}
                    onChange={(e) => setTopic(e.target.value)}
                    placeholder="Describe what you want to post about... e.g., 'morning workout routine' or 'new recipe I tried'"
                    className="min-h-24 bg-purple-500/10 border-purple-500/30 text-white placeholder:text-purple-300/50"
                    data-testid="topic-input"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Tone Selection */}
            <Card className="bg-black/30 border-purple-500/20 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <MessageSquare className="h-5 w-5 text-purple-400" />
                  Tone
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-2">
                  {tones.map((t) => (
                    <Button
                      key={t.id}
                      variant={tone === t.id ? 'default' : 'outline'}
                      className={tone === t.id 
                        ? 'bg-gradient-to-r from-purple-600 to-pink-600' 
                        : 'border-purple-500/30 text-purple-200 hover:bg-purple-500/20'}
                      onClick={() => setTone(t.id)}
                      data-testid={`tone-${t.id}`}
                    >
                      {t.emoji} {t.label}
                    </Button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Platform Selection */}
            <Card className="bg-black/30 border-purple-500/20 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Target className="h-5 w-5 text-blue-400" />
                  Optimize For
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {platforms.map((p) => (
                    <Badge
                      key={p.id}
                      variant={platform === p.id ? 'default' : 'outline'}
                      className={`cursor-pointer py-2 px-4 ${platform === p.id 
                        ? 'bg-purple-600 hover:bg-purple-500' 
                        : 'border-purple-500/30 text-purple-200 hover:bg-purple-500/20'}`}
                      onClick={() => setPlatform(p.id)}
                      data-testid={`platform-${p.id}`}
                    >
                      {p.label}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Button
              className="w-full h-14 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-lg font-semibold"
              onClick={handleGenerate}
              disabled={isGenerating}
              data-testid="generate-button"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="h-5 w-5 mr-2" />
                  Generate Content
                </>
              )}
            </Button>
          </div>

          {/* Output Section */}
          <div className="space-y-6">
            {generatedContent ? (
              <>
                {/* Caption */}
                <Card className="bg-black/30 border-purple-500/20 backdrop-blur-sm">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-white text-sm flex items-center gap-2">
                        <MessageSquare className="h-4 w-4 text-purple-400" />
                        Caption
                      </CardTitle>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="text-purple-300 hover:text-white"
                        onClick={() => copyToClipboard(generatedContent.caption, 'Caption')}
                        data-testid="copy-caption"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-white whitespace-pre-wrap" data-testid="generated-caption">
                      {generatedContent.caption}
                    </p>
                  </CardContent>
                </Card>

                {/* Hook */}
                {generatedContent.hook && (
                  <Card className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 border-amber-500/30 backdrop-blur-sm">
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-amber-200 text-sm flex items-center gap-2">
                          <Zap className="h-4 w-4" />
                          Attention Hook
                        </CardTitle>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="text-amber-300 hover:text-white"
                          onClick={() => copyToClipboard(generatedContent.hook, 'Hook')}
                          data-testid="copy-hook"
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-white font-medium" data-testid="generated-hook">
                        {generatedContent.hook}
                      </p>
                    </CardContent>
                  </Card>
                )}

                {/* Hashtags */}
                <Card className="bg-black/30 border-purple-500/20 backdrop-blur-sm">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-white text-sm flex items-center gap-2">
                        <Hash className="h-4 w-4 text-blue-400" />
                        Hashtags
                      </CardTitle>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="text-purple-300 hover:text-white"
                        onClick={() => copyToClipboard(generatedContent.hashtags, 'Hashtags')}
                        data-testid="copy-hashtags"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-blue-300" data-testid="generated-hashtags">
                      {generatedContent.hashtags}
                    </p>
                  </CardContent>
                </Card>

                {/* CTA */}
                {generatedContent.cta && (
                  <Card className="bg-black/30 border-purple-500/20 backdrop-blur-sm">
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-white text-sm flex items-center gap-2">
                          <Target className="h-4 w-4 text-green-400" />
                          Call to Action
                        </CardTitle>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="text-purple-300 hover:text-white"
                          onClick={() => copyToClipboard(generatedContent.cta, 'CTA')}
                          data-testid="copy-cta"
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-green-300" data-testid="generated-cta">
                        {generatedContent.cta}
                      </p>
                    </CardContent>
                  </Card>
                )}

                {/* Actions */}
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    className="flex-1 border-purple-500/50 text-purple-200 hover:bg-purple-500/20"
                    onClick={handleGenerate}
                    disabled={isGenerating}
                    data-testid="regenerate-button"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Regenerate
                  </Button>
                  <Button
                    className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500"
                    onClick={useContent}
                    data-testid="use-content-button"
                  >
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    Use This Content
                  </Button>
                </div>
              </>
            ) : (
              <Card className="bg-black/30 border-purple-500/20 backdrop-blur-sm h-full min-h-96">
                <CardContent className="h-full flex flex-col items-center justify-center p-8">
                  <div className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 p-6 rounded-full mb-4">
                    <Sparkles className="h-12 w-12 text-amber-400" />
                  </div>
                  <h3 className="text-white font-semibold text-lg mb-2">AI-Powered Content</h3>
                  <p className="text-purple-300/70 text-center text-sm">
                    Enter your topic and click generate to create viral captions, hashtags, and hooks instantly.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default DailyDropAIGenerator;
